type MobileControlsOptions = {
  onMove: (forward: number, rotate: number) => void;
  onTurretChange: (value: number) => void;
  onFireStart: () => void;
  onFireEnd: () => void;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return true;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
};

export default class MobileControls {
  private options: MobileControlsOptions;
  private leftWrapper: HTMLDivElement | null = null;
  private rightWrapper: HTMLDivElement | null = null;
  private leftThumb: HTMLDivElement | null = null;
  private rightThumb: HTMLDivElement | null = null;
  private movePointerId: number | null = null;
  private turretPointerId: number | null = null;
  private firePointerId: number | null = null;
  private turretValue: number = 0;
  private rafHandle: number | null = null;
  private boundMoveDown: (event: PointerEvent) => void;
  private boundMoveMove: (event: PointerEvent) => void;
  private boundMoveUp: (event: PointerEvent) => void;
  private boundTurretDown: (event: PointerEvent) => void;
  private boundTurretMove: (event: PointerEvent) => void;
  private boundTurretUp: (event: PointerEvent) => void;
  private boundGlobalPointerDown: (event: PointerEvent) => void;
  private boundGlobalPointerUp: (event: PointerEvent) => void;

  constructor(options: MobileControlsOptions) {
    this.options = options;

    this.boundMoveDown = (event) => this.handleMovePointerDown(event);
    this.boundMoveMove = (event) => this.handleMovePointerMove(event);
    this.boundMoveUp = (event) => this.handleMovePointerUp(event);
    this.boundTurretDown = (event) => this.handleTurretPointerDown(event);
    this.boundTurretMove = (event) => this.handleTurretPointerMove(event);
    this.boundTurretUp = (event) => this.handleTurretPointerUp(event);
    this.boundGlobalPointerDown = (event) => this.handleGlobalPointerDown(event);
    this.boundGlobalPointerUp = (event) => this.handleGlobalPointerUp(event);
  }

  attach() {
    const container = document.getElementById('ui-container');
    if (!container) {
      console.warn('[MobileControls] ui-container not found, skipping mobile controls setup.');
      return;
    }

    this.createJoysticks(container);

    this.leftWrapper?.addEventListener('pointerdown', this.boundMoveDown);
    this.leftWrapper?.addEventListener('pointermove', this.boundMoveMove);
    this.leftWrapper?.addEventListener('pointerup', this.boundMoveUp);
    this.leftWrapper?.addEventListener('pointercancel', this.boundMoveUp);
    this.leftWrapper?.addEventListener('pointerleave', this.boundMoveUp);

    this.rightWrapper?.addEventListener('pointerdown', this.boundTurretDown);
    this.rightWrapper?.addEventListener('pointermove', this.boundTurretMove);
    this.rightWrapper?.addEventListener('pointerup', this.boundTurretUp);
    this.rightWrapper?.addEventListener('pointercancel', this.boundTurretUp);
    this.rightWrapper?.addEventListener('pointerleave', this.boundTurretUp);

    window.addEventListener('pointerdown', this.boundGlobalPointerDown, { passive: false });
    window.addEventListener('pointerup', this.boundGlobalPointerUp, { passive: false });
    window.addEventListener('pointercancel', this.boundGlobalPointerUp, { passive: false });

    this.startTurretLoop();
  }

  detach() {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }

    window.removeEventListener('pointerdown', this.boundGlobalPointerDown);
    window.removeEventListener('pointerup', this.boundGlobalPointerUp);
    window.removeEventListener('pointercancel', this.boundGlobalPointerUp);

    if (this.leftWrapper) {
      this.leftWrapper.removeEventListener('pointerdown', this.boundMoveDown);
      this.leftWrapper.removeEventListener('pointermove', this.boundMoveMove);
      this.leftWrapper.removeEventListener('pointerup', this.boundMoveUp);
      this.leftWrapper.removeEventListener('pointercancel', this.boundMoveUp);
      this.leftWrapper.removeEventListener('pointerleave', this.boundMoveUp);
      this.leftWrapper.remove();
    }

    if (this.rightWrapper) {
      this.rightWrapper.removeEventListener('pointerdown', this.boundTurretDown);
      this.rightWrapper.removeEventListener('pointermove', this.boundTurretMove);
      this.rightWrapper.removeEventListener('pointerup', this.boundTurretUp);
      this.rightWrapper.removeEventListener('pointercancel', this.boundTurretUp);
      this.rightWrapper.remove();
    }

    this.leftWrapper = null;
    this.rightWrapper = null;
    this.leftThumb = null;
    this.rightThumb = null;
    this.movePointerId = null;
    this.turretPointerId = null;
    this.firePointerId = null;
  }

  private createJoysticks(container: HTMLElement) {
    const leftWrapper = document.createElement('div');
    leftWrapper.className = 'mobile-joystick-wrapper mobile-joystick-left';

    const leftBase = document.createElement('div');
    leftBase.className = 'mobile-joystick-base';
    const leftThumb = document.createElement('div');
    leftThumb.className = 'mobile-joystick-thumb';

    leftWrapper.appendChild(leftBase);
    leftWrapper.appendChild(leftThumb);

    const rightWrapper = document.createElement('div');
    rightWrapper.className = 'mobile-joystick-wrapper mobile-joystick-right';

    const rightBase = document.createElement('div');
    rightBase.className = 'mobile-joystick-base';
    const rightThumb = document.createElement('div');
    rightThumb.className = 'mobile-joystick-thumb';
    rightWrapper.appendChild(rightBase);
    rightWrapper.appendChild(rightThumb);

    container.appendChild(leftWrapper);
    container.appendChild(rightWrapper);

    this.leftWrapper = leftWrapper;
    this.leftThumb = leftThumb;
    this.rightWrapper = rightWrapper;
    this.rightThumb = rightThumb;
  }

  private handleMovePointerDown(event: PointerEvent) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (this.movePointerId !== null) return;
    event.preventDefault();
    this.movePointerId = event.pointerId;
    this.updateMoveJoystick(event);
  }

  private handleMovePointerMove(event: PointerEvent) {
    if (event.pointerId !== this.movePointerId) return;
    event.preventDefault();
    this.updateMoveJoystick(event);
  }

  private handleMovePointerUp(event: PointerEvent) {
    if (event.pointerId !== this.movePointerId) return;
    event.preventDefault();
    this.movePointerId = null;
    this.resetMoveJoystick();
  }

  private handleTurretPointerDown(event: PointerEvent) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (this.turretPointerId !== null) return;
    event.preventDefault();
    this.turretPointerId = event.pointerId;
    this.updateTurretJoystick(event);
  }

  private handleTurretPointerMove(event: PointerEvent) {
    if (event.pointerId !== this.turretPointerId) return;
    event.preventDefault();
    this.updateTurretJoystick(event);
  }

  private handleTurretPointerUp(event: PointerEvent) {
    if (event.pointerId !== this.turretPointerId) return;
    event.preventDefault();
    this.turretPointerId = null;
    this.resetTurretJoystick();
  }

  private handleGlobalPointerDown(event: PointerEvent) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (this.isJoystickPointer(event.pointerId, event.target as Element | null)) return;
    if (this.firePointerId !== null) return;
    this.firePointerId = event.pointerId;
    this.options.onFireStart();
  }

  private handleGlobalPointerUp(event: PointerEvent) {
    if (this.firePointerId !== event.pointerId) return;
    this.firePointerId = null;
    this.options.onFireEnd();
  }

  private isJoystickPointer(pointerId: number, target: Element | null) {
    if (!target) return false;
    if (pointerId === this.movePointerId || pointerId === this.turretPointerId) return true;
    if (this.leftWrapper && (this.leftWrapper === target || this.leftWrapper.contains(target))) return true;
    if (this.rightWrapper && (this.rightWrapper === target || this.rightWrapper.contains(target))) return true;
    return false;
  }

  private updateMoveJoystick(event: PointerEvent) {
    if (!this.leftWrapper || !this.leftThumb) return;
    const rect = this.leftWrapper.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const maxRadius = rect.width / 2;
    const dx = event.clientX - centerX;
    const dy = event.clientY - centerY;

    const angle = Math.atan2(dy, dx);
    const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxRadius);
    const clampedX = Math.cos(angle) * distance;
    const clampedY = Math.sin(angle) * distance;

    this.leftThumb.style.transform = `translate(${clampedX}px, ${clampedY}px)`;

    const normalizedX = clamp(clampedX / maxRadius, -1, 1);
    const normalizedY = clamp(clampedY / maxRadius, -1, 1);

    const forward = clamp(-normalizedY, -1, 1);
    const rotate = clamp(-normalizedX, -1, 1);

    this.options.onMove(forward, rotate);
  }

  private resetMoveJoystick() {
    if (this.leftThumb) {
      this.leftThumb.style.transform = 'translate(0px, 0px)';
    }
    this.options.onMove(0, 0);
  }

  private updateTurretJoystick(event: PointerEvent) {
    if (!this.rightWrapper || !this.rightThumb) return;
    const rect = this.rightWrapper.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const maxRadius = rect.width / 2;
    const dx = event.clientX - centerX;
    const dy = event.clientY - centerY;

    const angle = Math.atan2(dy, dx);
    const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxRadius);
    const clampedX = Math.cos(angle) * distance;
    const clampedY = Math.sin(angle) * distance;

    this.rightThumb.style.transform = `translate(${clampedX}px, ${clampedY}px)`;

    const normalizedX = clamp(clampedX / maxRadius, -1, 1);
    this.turretValue = clamp(-normalizedX, -1, 1);
  }

  private resetTurretJoystick() {
    if (this.rightThumb) {
      this.rightThumb.style.transform = 'translate(0px, 0px)';
    }
    this.turretValue = 0;
  }

  private startTurretLoop() {
    const loop = () => {
      if (Math.abs(this.turretValue) > 0.05) {
        this.options.onTurretChange(this.turretValue);
      } else {
        this.options.onTurretChange(0);
      }
      this.rafHandle = requestAnimationFrame(loop);
    };
    this.rafHandle = requestAnimationFrame(loop);
  }
}

